import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Form } from 'antd';
import { BaseSelect } from '../../../custom_shared/components';
import { useAppStore } from '../../stores';
import './BaseCompanySelectModal.less';

type FormValues = {
    selectedBaseCompanyId?: string;
};

const BaseCompanySelectModal: React.FC = () => {
    const appStore = useAppStore();

    const { baseCompanySegregationStore, actionStore, sessionStore } = appStore;

    const ref = React.useRef<HTMLDivElement>(null);

    const [form] = Form.useForm<FormValues>();

    const formId = `base-company-select-modal-${sessionStore.sessionId}`;

    const onValuesChange = async (changedFields: FormValues) => {
        if (!changedFields.selectedBaseCompanyId) {
            return;
        }

        await baseCompanySegregationStore.selectBaseCompany(changedFields.selectedBaseCompanyId);
        await actionStore.initializeContractIngestion();
    };

    if (baseCompanySegregationStore.state.error) {
        const getErrorTitle = () => {
            switch (baseCompanySegregationStore.state.error) {
                case 'NoBaseCompaniesAssigned':
                    return 'No Base Companies Assigned';
                case 'UserExternalIdNotSet':
                    return 'User External ID Not Set';
                default:
                    return 'Unknown Error';
            }
        };

        const getErrorText = () => {
            switch (baseCompanySegregationStore.state.error) {
                case 'NoBaseCompaniesAssigned':
                    return 'You currently have no Base Companies assigned to your account. Please contact your system administrator or support team to configure your access.';
                case 'UserExternalIdNotSet':
                    return 'Your user external ID is not set. Please contact your system administrator or support team to configure your access.';
                default:
                    return 'An unknown error occurred. Please contact your system administrator or support team for assistance.';
            }
        };

        return (
            <div ref={ref} className="contract-ingestion-base-company-select-modal-container">
                <Modal
                    title={getErrorTitle()}
                    visible={baseCompanySegregationStore.state.showBaseCompanySelectModal}
                    maskClosable={false}
                    closable={false}
                    width={400}
                    destroyOnClose
                    getContainer={() => ref.current as HTMLElement}
                    centered
                    footer={[
                        <Button key="continue-button" type="primary" onClick={() => appStore.dispose()}>
                            Close
                        </Button>
                    ]}
                >
                    <span className="explanation-text">{getErrorText()}</span>
                </Modal>
            </div>
        );
    }

    return (
        <div ref={ref} className="contract-ingestion-base-company-select-modal-container">
            <Modal
                title="Select Base Company"
                visible={baseCompanySegregationStore.state.showBaseCompanySelectModal}
                maskClosable={false}
                closable={false}
                width={400}
                destroyOnClose
                getContainer={() => ref.current as HTMLElement}
                centered
                footer={null}
            >
                <span className="explanation-text">
                    Choose your active base company to view associated data in the application.
                </span>

                <Form id={formId} form={form} layout="vertical" onValuesChange={onValuesChange}>
                    <Form.Item
                        name="selectedBaseCompanyId"
                        rules={[{ required: true, message: 'Please select a base company!' }]}
                    >
                        <BaseSelect
                            placeholder="Search base companies..."
                            className="base-company-select"
                            loading={baseCompanySegregationStore.loadingBaseCompanies}
                            options={baseCompanySegregationStore.baseCompanyOptions}
                            filterOption={(text, option) =>
                                option?.title && option.title.toLowerCase().includes(text.toLowerCase())
                            }
                            showSearch
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default observer(BaseCompanySelectModal);
